import { DialogAlert } from '@cellula/react';
import { DialogAlertService, DialogOptions, DialogOptionsWithResolver } from '@component/dialog_alert/dialog_alert.service';
import { useService } from '@visiba/instantiation';
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

const DialogAlertContext = createContext<null | ((options: DialogOptions) => void)>(null);

export function useDialogAlert() {
	return useContext(DialogAlertContext);
}

export function DialogAlertProvider({ children }: React.PropsWithChildren) {
	const dialogAlertService = useService(DialogAlertService);
	const [dialogQueue, setDialogQueue] = useState<DialogOptionsWithResolver[]>([]);
	const [isOpen, setIsOpen] = useState(false);

	useEffect(() => {
		const unsubscribe = dialogAlertService.subscribe((dialog) => {
			setDialogQueue((queue) => [...queue, dialog]);
		});

		return unsubscribe;
	}, [dialogAlertService]);

	useEffect(() => {
		if (dialogQueue.length > 0 && !isOpen) {
			setIsOpen(true);
		}
	}, [dialogQueue.length, isOpen]);

	const handleAbort = useCallback(() => {
		setIsOpen(false);

		const currentDialog = dialogQueue[0];
		if (currentDialog) {
			currentDialog.resolve();
			setDialogQueue((queue) => queue.slice(1));
		}
	}, [dialogQueue]);

	const showDialog = useCallback((props: DialogOptions) => dialogAlertService.show(props), [dialogAlertService]);

	const currentDialog = dialogQueue[0];

	return (
		<DialogAlertContext.Provider value={showDialog}>
			{children}
			{currentDialog && (
				<DialogAlert
					header={currentDialog.header}
					description={currentDialog.description}
					abortText={currentDialog.abortText}
					isOpen={isOpen}
					onAbort={handleAbort}
				/>
			)}
		</DialogAlertContext.Provider>
	);
}
